import { PersonalInfo, WorkExperience, ResearchExperience, Project } from '../types';

export const personalInfo: PersonalInfo = {
  name: "Spurthi Setty",
  title: "Machine Learning Engineer",
  location: "Hoboken, NJ",
  email: "settyspurthi@gmail.com",
  linkedin: "https://linkedin.com/in/spurthi-setty",
  github: "https://github.com/SpurthiSetty",
  education: "Stevens Institute of Technology, B.E. in Software Engineering, Certificate in ML, GPA: 4.0",
  skills: [
    "Python", "Java", "TensorFlow", "PyTorch", "HuggingFace", 
    "React", "Flask", "Firebase", "AWS", "LangChain"
  ],
  about: "I'm an aspiring Machine Learning Engineer with a strong foundation in software engineering and data science. I have research interests in Natural Language Processing, Computer Vision and Autonmous Systems and am passionate about bridging the gap between cutting-edge ML techniques and practical applications.",
  resumeUrl: "/resume.pdf"
};

export const workExperience: WorkExperience[] = [
  {
    id: "1",
    company: "GlobalFoundries",
    position: "Software Design Enablement Intern",
    duration: "May 2025 - Present",
    location: "Austin, TX",
    description: "Developed an asynchronous Python service using Model Context Protocol (MCP) to allow a local LLM (via Ollama) to call external REST API tools, streamlining design automation. Leveraged prompt engineering and knowledge graphs to improve intent parsing and tool invocation, reducing hallucination rates and improving UX.",
    technologies: ["Python", "MCP", "Ollama", "REST APIs", "Prompt Engineering", "Knowledge Graphs"],
    projects: [
      {
        id: "gf-1",
        title: "MCP-based LLM Service",
        description: "Built an asynchronous Python service that enables local LLMs to interact with external REST API tools through the Model Context Protocol, significantly improving design automation workflows.",
        technologies: ["Python", "MCP", "Ollama", "REST APIs"]
      }
    ]
  },
  {
    id: "2",
    company: "Anote.ai",
    position: "Founding ML Engineer & Project Manager",
    duration: "August 2023 - Present",
    location: "New York City, NY",
    description: "Led a team of 8+ engineers to train and benchmark object detection models (YOLOv8, DINO) on various datasets. Designed and presented agentic AI frameworks at public events. Built an SDK for CV/NLP tasks including Text Classification and NER. Contributed to open-source research in NLP and RAG.",
    technologies: ["Python", "PyTorch", "HuggingFace", "Ray", "OpenAI", "Gemini", "Claude", "LoRA", "SQL", "AWS", "SageMaker", "EC2"],
    links: [
      {
        type: "video",
        label: "Watch",
        url: "https://youtu.be/OgFijOrzarg?feature=shared",
        icon: "play"
      }
    ],
    projects: [
      {
        id: "anote-1",
        title: "Object Detection Benchmarking",
        description: "Led team to train and benchmark YOLOv8 and DINO models across multiple datasets, achieving state-of-the-art performance in object detection tasks.",
        technologies: ["Python", "PyTorch", "YOLOv8", "DINO"]
      },
      {
        id: "anote-2",
        title: "CV/NLP SDK",
        description: "Built comprehensive SDK for computer vision and natural language processing tasks including text classification and named entity recognition.",
        technologies: ["Python", "HuggingFace", "OpenAI", "Gemini"]
      }
    ]
  },
  {
    id: "3",
    company: "Medtronic",
    position: "Software Quality Engineering Intern",
    duration: "May 2024 - Aug 2024",
    location: "Boston, MA",
    description: "Built a full-stack app using Streamlit and Selenium to automate FMEA risk calculations, reducing manual effort by 90%. Conducted embedded software risk analysis using C++, Polarion, and Jama per FDA standards.",
    technologies: ["Python", "Selenium", "Streamlit", "C++", "JIRA", "Polarion", "Pandas"],
    projects: [
      {
        id: "medtronic-1",
        title: "FMEA Automation Tool",
        description: "Developed a full-stack application that automates Failure Mode and Effects Analysis risk calculations, significantly reducing manual effort and improving accuracy in medical device development.",
        technologies: ["Python", "Streamlit", "Selenium", "Pandas"]
      }
    ]
  },
  {
    id: "4",
    company: "Stevens Student Managed Investment Fund (SMIF)",
    position: "Factor Model Analyst",
    duration: "Sept 2023 - May 2024",
    location: "Hoboken, NJ",
    description: "Developed time series forecasting models for economic indicators using ARIMA. Applied statistical modeling and MPT for sector allocation strategies.",
    technologies: ["Python", "Pandas", "ARIMA", "YahooFinance", "Matplotlib"],
    projects: [
      {
        id: "smif-1",
        title: "Economic Forecasting Models",
        description: "Built time series forecasting models using ARIMA for economic indicators, enabling data-driven investment decisions and portfolio optimization.",
        technologies: ["Python", "Pandas", "ARIMA", "Matplotlib"]
      }
    ]
  }
];

export const researchExperience: ResearchExperience[] = [
  {
    id: "1",
    title: "Anote.ai Research",
    institution: "Anote.ai",
    duration: "August 2023 - Present",
    description: "Leading research initiatives in RAG pipelines, few-shot prompting, and LLM fine-tuning. Contributing to open-source research in NLP and retrieval-augmented generation.",
    technologies: ["Python", "PyTorch", "HuggingFace", "OpenAI", "AWS", "QLoRA", "Llama3", "Mistral"],
    projects: [
      {
        id: "anote-research-1",
        title: "Benchmarking RAG Pipelines",
        description: "Improved accuracy in RAG pipelines using zero-shot reranking with cross-encoders, achieving a 10% improvement in retrieval accuracy.",
        outcomes: ["10% improvement in RAG pipeline accuracy", "Published research paper", "Presented at academic conference"],
        links: [
          {
            type: "paper",
            label: "Paper",
            url: "https://arxiv.org/abs/2404.07221",
            icon: "document"
          },
          {
            type: "video",
            label: "Presentation",
            url: "https://youtu.be/0gqCpghZXEE?feature=shared",
            icon: "play"
          },
          {
            type: "github",
            label: "GitHub",
            url: "https://github.com/nv78/OpenAnote/tree/main/materials/research/researchcode/Benchmarking-RAG",
            icon: "code"
          }
        ]
      },
      {
        id: "anote-research-2",
        title: "Few-Shot Prompting + Fine-Tuning LLMs",
        description: "Used OpenAI APIs, QLoRA, and AWS to fine-tune Llama3 and Mistral models for improved performance on specific tasks.",
        outcomes: ["Successfully fine-tuned Llama3 and Mistral models", "Published research paper", "Presented findings at conference"],
        links: [
          {
            type: "paper",
            label: "Paper",
            url: "https://arxiv.org/abs/2402.01722",
            icon: "document"
          },
          {
            type: "video",
            label: "Presentation",
            url: "https://www.youtube.com/watch?v=mMmaTMuRZmo&ab_channel=Anote",
            icon: "play"
          },
          {
            type: "github",
            label: "GitHub",
            url: "https://github.com/nv78/OpenAnote/tree/main/materials/research/researchcode/Benchmarking-Question-Answering",
            icon: "code"
          }
        ]
      }
    ]
  },
  {
    id: "2",
    title: "Light and Life Laboratory",
    institution: "Stevens Institute of Technology",
    duration: "May 2023 - Aug 2023",
    location: "Hoboken, NJ",
    role: "Undergraduate Research Assistant",
    description: "Analyzed and preprocessed NASA satellite and sensor data for ingestion into deep learning pipelines. Contributed to expanding OC-SMART platform coverage by over 100%.",
    technologies: ["Python", "MATLAB", "TensorFlow", "Geospatial Processing", "NASA Data"],
    outcomes: [
      "Expanded OC-SMART platform coverage by >100%",
      "Processed and analyzed NASA satellite data",
      "Contributed to deep learning pipeline development"
    ],
    projects: [
      {
        id: "lll-1",
        title: "NASA Data Processing Pipeline",
        description: "Developed data processing pipelines for NASA satellite and sensor data, enabling efficient ingestion into deep learning models for oceanographic analysis.",
        technologies: ["Python", "MATLAB", "TensorFlow", "Geospatial Processing"]
      }
    ]
  }
];

export const projects: Project[] = [
  {
    id: "1",
    title: "ExchangeForStudents",
    description: "A React-based platform that leverages OpenAI Vision API to help students exchange textbooks and educational materials efficiently. Features intelligent image recognition for book identification and automated matching algorithms.",
    technologies: ["React", "OpenAI Vision API", "TypeScript", "Tailwind CSS", "Firebase"],
    githubUrl: "https://github.com/Thomas-Ung/ExchangeForStudents",
    liveUrl: "https://www.youtube.com/shorts/vmct60yD-4w"
  },
  {
    id: "2",
    title: "Mental Health Voice Assistant",
    description: "A Flask-based voice assistant that provides mental health support using OpenAI and Gemini APIs. Features natural language processing for empathetic responses and crisis detection capabilities.",
    technologies: ["Flask", "OpenAI API", "Gemini API", "Python", "NLP", "Voice Processing"],
    githubUrl: "https://github.com/cascadingluo/CS555-team-8-project"
  }
]; 